import type { WorkflowDefinition } from "../types";

/**
 * W28 — Read AI's trusted-source list and correct wrong domains.
 *
 * `shared`: this is a weekly reporting routine over property-wide state, not a
 * per-incident thing. Three of its seven steps are manual — reading a table,
 * cross-referencing publisher intelligence, and investigating a look-alike
 * domain are all judgement calls the product cannot observe.
 */
export const trustedSourcesWorkflow: WorkflowDefinition = {
	id: "trusted-sources",
	wNumber: 28,
	title: "See which sites AI trusts and fix the wrong ones",
	outcome:
		"See which sites AI reads before answering, and correct the wrong addresses.",
	audiences: ["pr", "inhouse"],
	uses: [
		"AI Consultations",
		"Citation Intelligence",
		"Settings",
		"Annotations",
	],
	scope: "property",
	concurrency: "shared",
	progress: "observed",
	isApplicable: (mode) => mode.showGroundingSearches,
	articleSlug:
		"how-to-see-which-sites-ai-trusts-and-fix-the-ones-it-gets-wrong",
	articleStatus: "published",
	status: "live",
	steps: [
		{
			id: "read-card",
			title: "Read the AI Consultations card",
			page: "AI Consultations",
			route: "/dashboard/consultations",
			criterion: "AI has consulted at least one site directly.",
			why: "Sometimes AI looks up one specific site before answering. When that site is yours, it is direct evidence of authority.",
			docsHref: "/docs/dashboards/brand-dashboard",
			tasks: [
				"Compare your own site against competitors in the counts",
				"Check the share of answers that consulted them",
			],
			complete: { kind: "count", key: "consultationCount", gte: 1 },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Sites AI consulted directly",
							value: state.property.consultationCount,
							target: 1,
						}
					: null,
		},
		{
			id: "pitch-targets",
			title: "Turn the consulted sites into pitch targets",
			page: "Citation Intelligence",
			route: "/citation-intelligence",
			criterion: "You've reviewed the third-party rows.",
			why: "A site AI reads directly outranks one it merely cites in passing. That is a better pitch list than domain authority.",
			docsHref: "/docs/dashboards/citation-intelligence",
			tasks: [
				"Cross-reference the sources AI consults directly",
				"Score the interesting ones before you pitch",
			],
			complete: { kind: "manual" },
		},
		{
			id: "investigate",
			title: "Check the look-alike domains",
			page: "AI Consultations",
			route: "/dashboard/consultations",
			criterion: "You've checked the flagged domains.",
			why: "A look-alike domain with a live site means your authority lands on someone else's address. An empty one means AI invented it.",
			docsHref: "/docs/dashboards/brand-dashboard",
			tasks: [
				"Open each flagged domain and see what is there",
				"Check the wrong-domain notes on the timeline",
			],
			complete: { kind: "manual" },
		},
		{
			id: "correct-in-product",
			title: "Claim or dismiss each wrong address",
			page: "Settings",
			route: "/settings",
			criterion: "You've claimed or dismissed a domain.",
			why: "Claiming a domain moves its history into your own numbers. Dismissing marks it as noise without claiming it.",
			docsHref: "/docs/dashboards/settings",
			tasks: [
				"Claim legacy, regional and acquired-brand domains as yours",
				"Dismiss the addresses that are simply noise",
			],
			complete: {
				kind: "any",
				of: [
					{ kind: "count", key: "additionalDomainCount", gte: 1 },
					{ kind: "count", key: "suppressedDomainCount", gte: 1 },
				],
			},
			signal: ({ state }) =>
				state.property
					? {
							kind: "checklist",
							items: [
								{
									label: `${state.property.additionalDomainCount} domains claimed as yours`,
									done:
										state.property.additionalDomainCount >
										0,
								},
								{
									label: `${state.property.suppressedDomainCount} dismissed as noise`,
									done:
										state.property.suppressedDomainCount >
										0,
								},
							],
						}
					: null,
		},
		{
			id: "correct-at-source",
			title: "Fix the address at the source",
			page: "Annotations",
			route: "/annotations",
			criterion: "You've noted the fix since you started.",
			why: "AI learned the wrong address somewhere. One canonical domain everywhere is the durable fix.",
			docsHref: "/docs/dashboards/annotations",
			tasks: [
				"Set one canonical address in your Organization schema",
				"Fix directory, social and press boilerplate addresses",
				"Note the date you made the change",
			],
			complete: { kind: "since", event: "annotationCreated" },
		},
		{
			id: "alerts",
			title: "Check who gets wrong-domain alerts",
			page: "Settings",
			route: "/settings?tab=notifications#wrong-domain-alerts",
			criterion: "At least one person gets these alerts.",
			why: "A new wrong domain should reach an inbox, not just the timeline. Everyone on the team gets these alerts unless you narrow the list.",
			docsHref: "/docs/dashboards/settings",
			tasks: [
				"Check the wrong-domain alerts list on the Notifications tab",
				"Add outside addresses, like an agency contact, if they should get them too",
			],
			complete: {
				kind: "count",
				key: "wrongDomainAlertRecipientCount",
				gte: 1,
			},
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "People who get these alerts",
							value: state.property
								.wrongDomainAlertRecipientCount,
							target: 1,
						}
					: null,
		},
		{
			id: "mcp-digest",
			title: "Automate the weekly summary",
			page: "AI Consultations",
			route: "/dashboard/consultations",
			criterion: "You've set up the weekly summary.",
			why: "The same numbers are available to your AI assistant. The weekly read can be a scheduled summary instead of a reminder.",
			docsHref: "/docs/mcp/account-data",
			tasks: [
				"Connect the Spyglasses MCP server",
				"Schedule a weekly summary comparing this week against last",
			],
			optional: true,
			complete: { kind: "manual" },
		},
	],
};
